import {Box, Button, Card, CTABanner, FAQ, FAQGroup, Grid, Section, SectionIntro, Text} from '@primer/react-brand'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {renderWithNoMarks} from '../../../utils'
import type {OutroSectionProps} from './types'
import overviewStyles from '../../OverviewTemplate.module.css'

export const OutroSection = ({
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroFaqsHeading,
}: OutroSectionProps) => {
  const groupsWithFaqs = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const storySpan = outroCustomerStories && outroCustomerStories.length > 2 ? 4 : 6

  return (
    <Box marginBlockStart={48}>
      <Section paddingBlockStart="none" className={overviewStyles.gridFrameTop} fullWidth>
        {hasCustomerStories && (
          <Box className={`${overviewStyles.gridFrameBottom}`}>
            <Grid className={overviewStyles.gridContent}>
              {outroCustomerStories.map(story => {
                const {logo, lead, url, title} = story.fields
                return (
                  <Grid.Column
                    key={story.sys.id}
                    span={{xsmall: 12, medium: 6, large: storySpan}}
                    className="gridColumn"
                  >
                    <Box
                      className={overviewStyles.gridItem}
                      padding={{narrow: 24, regular: 40}}
                      paddingBlockStart={{regular: 80}}
                      paddingBlockEnd={{regular: 80}}
                    >
                      <Card href={url} variant="minimal" fullWidth>
                        <Card.Image
                          src={`${logo.fields.file.url}?fm=webp&h=60`}
                          alt={logo.fields.description || title}
                          style={{height: '30px', width: 'auto', objectFit: 'contain'}}
                        />
                        <Card.Heading>{title}</Card.Heading>
                        <Card.Description>{lead}</Card.Description>
                      </Card>
                    </Box>
                  </Grid.Column>
                )
              })}
            </Grid>
          </Box>
        )}

        {outroCtaBanner && (
          <CTABanner>
            {outroCtaHeading && <CTABanner.Heading>{renderWithNoMarks(outroCtaHeading)}</CTABanner.Heading>}
            {outroCtaDescription && (
              <CTABanner.Description>{renderWithNoMarks(outroCtaDescription)}</CTABanner.Description>
            )}
            {outroCtaOne?.fields.href && outroCtaTwo?.fields.href ? (
              <CTABanner.ButtonGroup>
                <Button as="a" href={outroCtaOne.fields.href}>
                  {outroCtaOne.fields.text}
                </Button>
                <Button as="a" href={outroCtaTwo.fields.href} variant="secondary">
                  {outroCtaTwo.fields.text}
                </Button>
              </CTABanner.ButtonGroup>
            ) : outroCtaOne?.fields.href ? (
              <CTABanner.ButtonGroup>
                <Button as="a" href={outroCtaOne.fields.href}>
                  {outroCtaOne.fields.text}
                </Button>
              </CTABanner.ButtonGroup>
            ) : null}
          </CTABanner>
        )}

        {outroIncludeResources && (
          <>
            {outroResourcesHeading && (
              <Box marginBlockStart={48} marginBlockEnd={48}>
                <SectionIntro align="center">
                  <SectionIntro.Heading>{renderWithNoMarks(outroResourcesHeading)}</SectionIntro.Heading>
                </SectionIntro>
              </Box>
            )}
            <Box className={`${overviewStyles.gridFrameTop} ${overviewStyles.gridFrameBottom}`}>
              <Grid columnGap="none" className={overviewStyles.gridContent}>
                {outroResources?.map(resource => {
                  const Octicon = getPrimerIcon(resource.fields.icon)
                  return (
                    <Grid.Column key={resource.sys.id} span={{medium: 4}} className="gridColumn">
                      <Box
                        className={overviewStyles.gridItem}
                        padding={{narrow: 24, regular: 40}}
                        paddingBlockStart={{regular: 80}}
                        paddingBlockEnd={{regular: 80}}
                        borderWidth="thin"
                        borderColor="muted"
                      >
                        <Card href={resource.fields.href} variant="minimal" fullWidth>
                          {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                          <Card.Heading>{renderWithNoMarks(resource.fields.heading)}</Card.Heading>
                          <Card.Description>{renderWithNoMarks(resource.fields.description)}</Card.Description>
                        </Card>
                      </Box>
                    </Grid.Column>
                  )
                })}
              </Grid>
            </Box>
          </>
        )}

        {groupsWithFaqs && groupsWithFaqs.length > 0 && (
          <Box paddingBlockStart={48}>
            <FAQGroup variant="gridline" defaultSelectedIndex={1}>
              {outroFaqsHeading && <FAQGroup.Heading>{renderWithNoMarks(outroFaqsHeading)}</FAQGroup.Heading>}
              {groupsWithFaqs.map(group => {
                const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

                return (
                  <FAQ key={group.sys.id} variant="gridline">
                    {group.fields.heading && <FAQ.Heading>{group.fields.heading}</FAQ.Heading>}
                    {completeFaqs.map((faq, index) => (
                      <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                        <FAQ.Question>{faq.fields.question}</FAQ.Question>
                        <FAQ.Answer>
                          <Text as="p" variant="muted">
                            {renderWithNoMarks(faq.fields.answer)}
                          </Text>
                        </FAQ.Answer>
                      </FAQ.Item>
                    ))}
                  </FAQ>
                )
              })}
            </FAQGroup>
          </Box>
        )}
      </Section>
    </Box>
  )
}
